import React, { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'react-bootstrap';

export const ROLE_OPTIONS = [
  ['all', 'All roles'],
  ['expert', 'Experts only'],
  ['pr', 'PRs only'],
];
export const FORMAT_OPTIONS = [
  ['all', 'Show all'],
  ['written', 'Written only'],
  ['video', 'Video only'],
];
export const DEFAULT_FILTERS = { role: 'all', format: 'all' };

export const filtersActive = (filters) => filters.role !== 'all' || filters.format !== 'all';

// A hybrid account is both an expert and a PR, so it stays under either role filter
const roleMatches = (pitch, role) => {
  if (role === 'all') { return true; }
  const type = pitch.type_of_profile || '';
  if (type.endsWith('promoter_hybrid')) { return true; }
  return type.endsWith(role === 'expert' ? 'promoter_expert' : 'promoter_pr');
};

// has_video is the pitches API column (recorded or uploaded video); the attachment type is the fallback until it is deployed
export const pitchHasVideo = (pitch) => pitch.has_video === true
  || (pitch.pitch_attachments || []).some((a) => a.type === 'Interact::Attachment::Video');

const formatMatches = (pitch, format) => format === 'all' || (format === 'video') === pitchHasVideo(pitch);

export const matchesFilters = (pitch, filters) => roleMatches(pitch, filters.role)
  && formatMatches(pitch, filters.format);

const ChipToggle = forwardRef(({
  onClick, label, active, 'aria-expanded': expanded,
}, ref) => (
  <button
    type="button"
    ref={ref}
    className={`pi-chip ${active ? 'active' : ''}`}
    onClick={onClick}
    aria-expanded={expanded}
  >
    {label} <i className="fa-regular fa-chevron-down" aria-hidden="true" />
  </button>
));
ChipToggle.displayName = 'ChipToggle';
ChipToggle.propTypes = {
  onClick: PropTypes.func,
  label: PropTypes.string,
  active: PropTypes.bool,
  'aria-expanded': PropTypes.bool,
};

// One chip: the popover holds a pending choice that only applies on Apply (as designed)
function FilterChip({
  id, name, title, options, value, onApply,
}) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(value);
  const label = value === 'all' ? name : options.find(([key]) => key === value)[1];

  return (
    <Dropdown
      show={open}
      autoClose="outside"
      onToggle={(next) => {
        if (next) { setPending(value); }
        setOpen(next);
      }}
    >
      <Dropdown.Toggle as={ChipToggle} label={label} active={open || value !== 'all'} />
      <Dropdown.Menu className="pi-filter-menu" role="dialog" aria-label={title}>
        <div className="pi-filter-title">{title}</div>
        {options.map(([key, text]) => (
          <div className="form-check" key={key}>
            <input
              className="form-check-input"
              type="radio"
              name={`pi-filter-${id}`}
              id={`pi-filter-${id}-${key}`}
              checked={pending === key}
              onChange={() => setPending(key)}
            />
            <label className="form-check-label" htmlFor={`pi-filter-${id}-${key}`}>
              {text}
            </label>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-primary btn-sm w-100 mt-2"
          onClick={() => {
            onApply(pending);
            setOpen(false);
          }}
        >
          Apply
        </button>
      </Dropdown.Menu>
    </Dropdown>
  );
}

FilterChip.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  value: PropTypes.string.isRequired,
  onApply: PropTypes.func.isRequired,
};

function PitchFilters({ filters, onChange }) {
  return (
    <div className="pi-filters" role="group" aria-label="Filter pitches">
      <FilterChip
        id="role"
        name="Role"
        title="Select Role"
        options={ROLE_OPTIONS}
        value={filters.role}
        onApply={(role) => onChange({ ...filters, role })}
      />
      <FilterChip
        id="format"
        name="Pitch Format"
        title="Select Pitch Format"
        options={FORMAT_OPTIONS}
        value={filters.format}
        onApply={(format) => onChange({ ...filters, format })}
      />
    </div>
  );
}

PitchFilters.propTypes = {
  filters: PropTypes.shape({ role: PropTypes.string.isRequired, format: PropTypes.string.isRequired }).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default PitchFilters;
